import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import AccessDenied from './components/AccessDenied';

import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import customOktaAuth from './utils/customOktaAuth';

// Auth Context
export const AuthContext = React.createContext();

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [authenticated, setAuthenticated] = useState(() => customOktaAuth.isAuthenticated());
  const [hasAccess, setHasAccess] = useState(null); // null = checking, true = has access, false = no access
  const hasTriedLogin = useRef(false);
  const checkTimeoutRef = useRef(null);

  useEffect(() => {
    // Re-check authentication state periodically to catch token storage
    const checkAuth = async () => {
      const isAuth = customOktaAuth.isAuthenticated();
      setAuthenticated(isAuth);
      
      // Only redirect to login if user is NOT authenticated
      // Don't redirect if user is authenticated but lacks groups (they'll see AccessDenied instead)
      if (!isAuth && !hasTriedLogin.current && location.pathname !== '/callback') {
        // Add a small delay to prevent immediate redirect loops
        checkTimeoutRef.current = setTimeout(() => {
          // Double-check authentication before redirecting
          if (!customOktaAuth.isAuthenticated()) {
            console.log('[ProtectedRoute] User not authenticated, redirecting to login');
            hasTriedLogin.current = true;
            customOktaAuth.login();
          }
        }, 500);
      } else if (isAuth) {
        // User is authenticated - check if they have required access
        try {
          const access = customOktaAuth.hasRequiredAccess();
          setHasAccess(access);
          console.log('[ProtectedRoute] User authenticated, access check:', access);
          
          if (access === false) {
            console.log('[ProtectedRoute] User lacks required groups (App-VISION-User or App-VISION-Admin)');
            console.log('[ProtectedRoute] Showing AccessDenied page - user should request access via ServiceNow');
          }
        } catch (error) {
          console.error('[ProtectedRoute] Error checking access:', error);
          // On error, default to denying access
          setHasAccess(false);
        }
      } else {
        // Not authenticated, set hasAccess to false to prevent infinite spinner
        setHasAccess(false);
      }
    };

    // Initial check
    checkAuth();
    
    // Check again after a short delay (in case tokens are being stored)
    const delayCheck = setTimeout(checkAuth, 100);
    
    // Add a timeout to prevent infinite spinner (max 5 seconds)
    const timeoutCheck = setTimeout(() => {
      if (hasAccess === null) {
        console.warn('[ProtectedRoute] Access check timed out, defaulting to false');
        setHasAccess(false);
      }
    }, 5000);
    
    return () => {
      clearTimeout(delayCheck);
      clearTimeout(timeoutCheck);
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [location.pathname, hasAccess]);

  if (!authenticated) {
    return (
      <div className="flex h-screen bg-dark-bg items-center justify-center">
        <div className="w-16 h-16 border-4 border-cyan-accent border-t-transparent rounded-full animate-spin"
             style={{ boxShadow: '0 0 30px rgba(0, 229, 255, 0.5)' }} />
      </div>
    );
  }

  // If still checking access, show loading
  if (hasAccess === null) {
    return (
      <div className="flex h-screen bg-dark-bg items-center justify-center">
        <div className="w-16 h-16 border-4 border-cyan-accent border-t-transparent rounded-full animate-spin"
             style={{ boxShadow: '0 0 30px rgba(0, 229, 255, 0.5)' }} />
      </div>
    );
  }

  // If user doesn't have required access, show access denied page
  if (hasAccess === false) {
    return <AccessDenied />;
  }

  return children;
};

// Callback Handler
const CallbackHandler = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hasCode = urlParams.has('code');
      const hasState = urlParams.has('state');
      const hasError = urlParams.has('error');
      
      console.log('Callback handler - URL:', window.location.href);
      console.log('Callback handler - Has code:', hasCode, 'Has state:', hasState, 'Has error:', hasError);
      console.log('Callback handler - Query string:', window.location.search);
      
      // Check for Okta error responses first
      if (hasError) {
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description') || 'Unknown error';
        console.error('Okta error response:', error, errorDescription);
        handledRef.current = true;
        setError(`${error}: ${decodeURIComponent(errorDescription)}`);
        customOktaAuth.clearTokens();
        return;
      }
      
      // Check if already authenticated first
      if (customOktaAuth.isAuthenticated()) {
        console.log('Already authenticated, redirecting to home...');
        handledRef.current = true;
        navigate('/', { replace: true });
        return;
      }
      
      // If no OAuth params, user might have navigated here directly
      // Redirect to login instead of home to avoid loop
      if (!hasCode || !hasState) {
        console.log('No OAuth callback params found. Current URL:', window.location.href);
        console.log('Redirecting to login...');
        handledRef.current = true;
        // Clear any stale state and redirect to login
        customOktaAuth.clearTokens();
        customOktaAuth.login();
        return;
      }
      
      // Mark as handled before async operation to prevent double execution
      handledRef.current = true;
      
      try {
        console.log('Starting callback handler with OAuth params...');
        await customOktaAuth.handleCallback();
        console.log('Callback successful, tokens stored, redirecting...');
        
        // Use navigate instead of window.location.href to avoid full page reload
        // This prevents timing issues with token storage
        navigate('/', { replace: true });
      } catch (err) {
        console.error('Callback error:', err);
        setError(err.message || 'Authentication failed');
        handledRef.current = false;
        // Clear any partial auth state on error
        customOktaAuth.clearTokens();
      }
    };
    
    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="flex h-screen bg-dark-bg items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-red-500 mb-4">Authentication Error</h2>
          <p className="text-gray-400">{error}</p>
          <button 
            onClick={() => customOktaAuth.login()}
            className="mt-4 px-6 py-2 bg-cyan-accent text-dark-bg rounded"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-dark-bg items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-cyan-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"
             style={{ boxShadow: '0 0 30px rgba(0, 229, 255, 0.5)' }} />
        <p className="text-cyan-accent">Completing login...</p>
      </div>
    </div>
  );
};

// App Wrapper with Auth Context
const AppWrapper = () => {
  return (
    <AuthContext.Provider value={{ auth: customOktaAuth }}>
      <BrowserRouter>
        <Routes>
          <Route path="/callback" element={<CallbackHandler />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <App />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AppWrapper />
  </React.StrictMode>
);
